// Where the game server is, from the page's point of view.
//
// In dev (any host, including a phone on the LAN) Vite proxies /ws and /api to the local server,
// and so does `vite preview` on localhost. A production build anywhere else must be told the
// server's address at build time (VITE_SERVER_URL, a wss:// URL), or the page says so instead
// of retrying forever.

export interface ServerAddress {
  /** WebSocket origin, e.g. wss://play.example.com */
  ws: string;
  /** HTTP origin for /api, e.g. https://play.example.com */
  http: string;
}

export function isLocalHostname(hostname: string): boolean {
  return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(hostname) || hostname.endsWith(".localhost");
}

export function serverAddress(): ServerAddress | null {
  const configured = (import.meta.env.VITE_SERVER_URL as string | undefined)?.replace(/\/$/, "");
  if (configured) {
    return { ws: configured, http: configured.replace(/^ws(s?):/, "http$1:") };
  }
  const isLocalHost = isLocalHostname(location.hostname);
  if (import.meta.env.DEV || isLocalHost) {
    const secure = location.protocol === "https:";
    return { ws: `${secure ? "wss" : "ws"}://${location.host}`, http: `${location.protocol}//${location.host}` };
  }
  return null;
}

export const NO_SERVER_MESSAGE = "no game server configured yet";

/**
 * Whether the Create tab is locked. The public demo (a production build on a public host,
 * hazycausation.com) shows Create but does not generate, because every build spends model
 * time; dev and localhost are unlocked so games can be made there. VITE_CREATE_LOCKED at build
 * time overrides: "1" locks, "0" unlocks.
 */
export function isCreateLocked(env: { PROD?: boolean; VITE_CREATE_LOCKED?: string }, hostname: string): boolean {
  if (env.VITE_CREATE_LOCKED === "1") return true;
  if (env.VITE_CREATE_LOCKED === "0") return false;
  return !!env.PROD && !isLocalHostname(hostname);
}

export function createLocked(): boolean {
  return isCreateLocked(import.meta.env as { PROD?: boolean; VITE_CREATE_LOCKED?: string }, location.hostname);
}
